'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useId, useRef, useState, useTransition, type KeyboardEvent, type ReactNode } from 'react'
import { ChevronDown, LogOut } from 'lucide-react'

import { cn } from '../cn'
import { Avatar } from './Avatar'
import { FrameLink } from './config'
import { FRAME_SETTINGS_ROUTES, postFrameSetting } from './settings'

export type AccountMenuItem = { href: string; label: string; icon: ReactNode }

/**
 * The account menu at the right of the top bar, after the design system's
 * AccountMenu: the avatar with name and role as the trigger, and below it the
 * account, the places that belong to it, the language and signing out — in
 * that fixed order. Administration lives here rather than in a row of icons.
 *
 * Esc, a click outside and every choice close it; the arrow keys move through
 * the entries. The language buttons post to the host's settings route, which
 * writes the account and the cookie like the settings page does.
 */
export function AccountMenu({
  name,
  email,
  role,
  items,
  language,
  languages,
  labels
}: {
  name: string
  email: string | null
  role: string
  items: readonly AccountMenuItem[]
  language: string
  /** The languages offered, in order. */
  languages: readonly string[]
  labels: { menu: string; menuOf: string; language: string; signOut: string }
}) {
  const id = useId()
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [pending, startTransition] = useTransition()
  const router = useRouter()

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    // Focus the first entry, so the keyboard lands in the menu it opened.
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const close = () => {
    setOpen(false)
    trigger.current?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const entries = Array.from(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    if (entries.length === 0) return
    e.preventDefault()
    const at = entries.indexOf(document.activeElement as HTMLElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % entries.length : (at - 1 + entries.length) % entries.length
    entries[next].focus()
  }

  return (
    <div ref={root} className="av-pop av-account__pop" onKeyDown={onKeyDown}>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        aria-label={labels.menuOf.replace('{name}', name)}
        className="av-account__trigger"
        onClick={() => setOpen(v => !v)}
      >
        <span className="av-account__tavatar">
          <Avatar name={name} size={32} />
        </span>
        <span className="av-account__text">
          <span className="av-account__tname">{name}</span>
          <span className="av-account__trole">{role}</span>
        </span>
        <ChevronDown className="av-account__chev size-3.5" aria-hidden />
      </button>

      {open && (
        <div id={`${id}-panel`} className="av-pop__panel av-pop__panel--bottom av-pop__panel--end" style={{ width: 272 }}>
          <div className="av-account">
            <div className="av-account__block">
              <Avatar name={name} size={40} />
              <div className="av-account__who">
                <div className="av-account__name">{name}</div>
                {email !== null && email !== name && <div className="av-account__email">{email}</div>}
                <div className="av-account__role">{role}</div>
              </div>
            </div>
            <div className="av-account__body">
              <ul className="av-pop__menu" role="menu" aria-label={labels.menu}>
                {items.map(item => (
                  <li key={item.href} role="none">
                    <FrameLink href={item.href} role="menuitem" className="av-pop__item" onClick={() => setOpen(false)}>
                      <span className="av-icon text-ink-muted [&>svg]:size-4">{item.icon}</span>
                      <span className="av-pop__item-text">{item.label}</span>
                    </FrameLink>
                  </li>
                ))}
              </ul>
              <div className="av-account__langrow">
                <span className="av-account__langlabel" id={`${id}-lang`}>
                  {labels.language}
                </span>
                <div className="av-account__langs" role="group" aria-labelledby={`${id}-lang`}>
                  {languages.map(locale => (
                    <button
                      key={locale}
                      type="button"
                      disabled={pending}
                      aria-pressed={locale === language}
                      className={cn('av-account__lang', locale === language && 'is-active')}
                      onClick={() =>
                        startTransition(async () => {
                          if (await postFrameSetting(FRAME_SETTINGS_ROUTES.locale, locale)) router.refresh()
                        })
                      }
                    >
                      {locale.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="av-pop__sep" role="separator" />
              <form action="/api/auth/signout" method="post">
                <ul className="av-pop__menu" role="menu" aria-label={labels.signOut}>
                  <li role="none">
                    <button type="submit" role="menuitem" className="av-pop__item">
                      <LogOut className="size-4 text-ink-muted" aria-hidden />
                      <span className="av-pop__item-text">{labels.signOut}</span>
                    </button>
                  </li>
                </ul>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
